import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/storefront/Header";
import Footer from "@/components/storefront/Footer";
import BagDrawer from "@/components/storefront/BagDrawer";
import SearchOverlay from "@/components/storefront/SearchOverlay";
import MusicPlayer from "@/components/storefront/MusicPlayer";
import ConsentBanner from "@/components/storefront/ConsentBanner";
import { CurrencyHint } from "@/components/storefront/CurrencyHint";
import DoorsOpen from "@/components/storefront/DoorsOpen";
import ComingSoon from "@/pages/ComingSoon";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";

const STAFF_ROLES = ["owner", "store_manager", "content", "client_care"];

const Splash = () => <div data-testid="app-splash" className="min-h-screen bg-cocoa" />;

// Premium momentum scrolling — a heavier, deliberate feel across the storefront.
const SmoothScroll = () => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  useEffect(() => { lenisRef.current?.scrollTo(0, { immediate: true }); }, [pathname]);
  return null;
};

const StorefrontLayout = () => {
  const { settings } = useStore();
  const { user } = useAuth();

  const settingsLoaded = settings !== null && settings !== undefined;
  const authChecked = user !== null; // false (guest) or object once resolved
  const isStaff = user && STAFF_ROLES.includes(user.role);

  // Launch time-gate: the store opens itself the moment the launch date/time
  // passes — the owner does not need to flip anything manually on launch day.
  const launchAt = settingsLoaded && settings.launch_at ? new Date(settings.launch_at).getTime() : null;
  const [launchPassed, setLaunchPassed] = useState(launchAt ? Date.now() >= launchAt : false);
  useEffect(() => {
    if (!launchAt) { setLaunchPassed(false); return; }
    let timer;
    // Re-check on a bounded delay: a single setTimeout for a far-future date
    // overflows the browser's 32-bit timer and fires immediately, so we cap it.
    const check = () => {
      const remaining = launchAt - Date.now();
      if (remaining <= 0) { setLaunchPassed(true); return; }
      setLaunchPassed(false);
      timer = setTimeout(check, Math.min(remaining, 43200000)); // re-arm at most every 12h
    };
    check();
    return () => clearTimeout(timer);
  }, [launchAt]);

  // Launch mode holds visitors on the countdown ONLY until the launch time passes.
  const launchOn = settingsLoaded && settings.launch_mode_enabled && !launchPassed;

  // Staff can preview exactly what visitors see on the countdown page, without
  // touching the real launch date (toggled from Admin → Launch Mode).
  let previewCountdown = false;
  try { previewCountdown = JSON.parse(localStorage.getItem("nba_preview_countdown")) === true; } catch {}
  const exitPreview = () => {
    try { localStorage.removeItem("nba_preview_countdown"); } catch {}
    window.location.href = "/";
  };

  // Base page background follows launch state: dark during the countdown, ivory once live.
  useEffect(() => {
    if (!settingsLoaded) return;
    document.body.style.backgroundColor = launchOn ? "#3A211A" : "#F4EFE6";
  }, [settingsLoaded, launchOn]);

  // Hold on a neutral dark screen until we know launch + auth state,
  // so visitors never see a flash of the store before the countdown.
  if (!settingsLoaded) return <Splash />;
  if (launchOn && !authChecked) return <Splash />;
  if (isStaff && previewCountdown) {
    return (
      <>
        <ComingSoon />
        <button
          onClick={exitPreview}
          data-testid="exit-countdown-preview"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gold text-cocoa px-6 py-2.5 text-[0.62rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors shadow-lg">
          Exit countdown preview
        </button>
      </>
    );
  }
  if (launchOn && !isStaff) return <ComingSoon />;

  return (
    <div className="App overflow-x-clip bg-ivory text-espresso min-h-screen">
      <SmoothScroll />
      {launchOn && isStaff && (
        <div data-testid="launch-preview-banner" className="bg-gold text-cocoa text-center py-2 px-4 text-[0.66rem] tracking-[0.16em] uppercase">
          Launch mode is ON — visitors see the coming-soon page. You're previewing the live store.
        </div>
      )}
      <Header />
      <main><Outlet /></main>
      <Footer />
      <BagDrawer />
      <SearchOverlay />
      <MusicPlayer />
      <ConsentBanner />
      <CurrencyHint />
      <DoorsOpen />
    </div>
  );
};

export default StorefrontLayout;
